// packages/i18n/src/config.ts
// Type-only imports: @tourtask/types reaches this file from apps (mobile-chat) that cannot resolve other packages.
import type { AppLocale } from '@tourtask/types/i18n';

/**
 * The UI locales. Adding one: follow docs/i18n-adding-a-locale.md, append a bare two-letter
 * code at the END (every derived order follows this list; database locale columns are CHAR(2)),
 * then fill the LOCALE_META row and the catalogs that tsc reports.
 */
export const locales = [
  'en',
  'es',
  'pt',
  'fr',
  'de',
  'it',
  'ar',
  'ja',
  'zh',
  'nl',
  'ru',
  'sv',
  'ca'
] as const;

export const defaultLocale = 'en';

/**
 * Global default IANA timeZone handed to next-intl (server request config +
 * client provider).
 *
 * Without a configured `timeZone`, next-intl falls back to the runtime's zone
 * (the `ENVIRONMENT_FALLBACK` warning) — which differs between the SSR host and
 * the browser and produces hydration markup mismatches. This app never renders
 * business data through next-intl's formatter: calendar dates go through
 * `formatDate` / `formatYmdDate` with an explicit business timezone, and the
 * lone `useFormatter` call passes its own `timeZone`. So this value is only a
 * deterministic fallback for incidental formatting.
 *
 * UTC is chosen deliberately: the product is multi-tenant with businesses in
 * many zones, so there is no single correct *local* default. UTC keeps SSR and
 * client identical and never renders a plausible-but-wrong local wall-clock time
 * — anything business-local must pass its own zone explicitly (the convention
 * the codebase already follows).
 */
export const defaultTimeZone = 'UTC';

export const supportedLocales = locales;

/** A record with one value per UI locale. */
export function mapLocales<T>(valueFor: (locale: AppLocale) => T): Record<AppLocale, T> {
  // Exhaustive by construction (one entry per `locales` member); Object.fromEntries cannot type that.
  return Object.fromEntries(locales.map((locale) => [locale, valueFor(locale)])) as Record<AppLocale, T>;
}

export function isSupportedLang(code: string): code is AppLocale {
  return supportedLocales.includes(code as AppLocale);
}

export function normalizeLocale(code: string): AppLocale {
  if (isSupportedLang(code)) {
    return code;
  }
  return fallbackLang;
}

/**
 * Legacy site-language codes that were renamed in the modern locale set. The
 * legacy PHP app used `cmn` (ISO 639-3 for Mandarin) for Simplified Chinese;
 * the modern app uses `zh`. Legacy-authored content (`*.strings` JSON, config
 * `site_languages_json`) still carries the old code. A `Map`, not an object
 * literal: a lookup by an arbitrary code must never reach `Object.prototype`
 * (`constructor`, `__proto__`), and some codes come from request headers.
 */
const LEGACY_LOCALE_ALIASES: ReadonlyMap<string, AppLocale> = new Map<string, AppLocale>([['cmn', 'zh']]);

/**
 * Map a possibly-legacy site-language code to its modern {@link AppLocale}, or
 * `null` when it is neither a supported locale nor a known legacy alias. Use at
 * the legacy→modern boundary (backfills, importers) where an unrecognized code
 * must be SKIPPED rather than silently relabelled — unlike {@link normalizeLocale},
 * which falls back to `en` and would mislabel content.
 */
export function canonicalizeLegacyLocale(code: string): AppLocale | null {
  if (isSupportedLang(code)) return code;
  return LEGACY_LOCALE_ALIASES.get(code) ?? null;
}

export const fallbackLang: AppLocale = 'en';

export type { AppLocale };